import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Calculator, Send, Activity, Sparkles, TrendingDown } from "lucide-react";
import { api } from "@/lib/api";
import { Panel, AnimatedNumber } from "@/components/common/Widgets";
import { fmtNum, fmtINR, fmtLakh, signPct } from "@/lib/format";

export default function AiAdvisor() {
  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-4 animate-fade-up" data-testid="ai-advisor">
      <div>
        <div className="eyebrow">AI Advisor · Predictive & Prescriptive</div>
        <h1 className="font-display font-extrabold text-2xl uppercase tracking-tight text-ink">Decision Engine</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CoalPredictor />
        <DeviationInvestigation />
      </div>

      <AdvisorChat />
    </div>
  );
}

function CoalPredictor() {
  const [plan, setPlan] = useState({ meg: 820, deg: 280, teg: 145 });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try { setResult(await api.predict({ meg: Number(plan.meg), deg: Number(plan.deg), teg: Number(plan.teg) })); }
    finally { setLoading(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  return (
    <Panel eyebrow="Hero · Tomorrow's Plan" title="Coal Requirement Predictor" testid="coal-predictor"
      right={<Calculator size={16} className="text-brand-green" />}>
      <div className="grid grid-cols-3 gap-3 mb-3">
        {["meg", "deg", "teg"].map((k) => (
          <div key={k}>
            <div className="eyebrow mb-1">{k.toUpperCase()} (MT)</div>
            <input type="number" value={plan[k]} data-testid={`predict-input-${k}`}
              onChange={(e) => setPlan({ ...plan, [k]: e.target.value })}
              className="w-full panel-2 px-3 py-2 text-ink font-mono text-sm focus:outline-none focus:border-brand-green" />
          </div>
        ))}
      </div>
      <button onClick={run} disabled={loading} data-testid="predict-run"
        className="w-full flex items-center justify-center gap-2 py-2.5 font-display font-bold uppercase tracking-wider text-sm transition-colors"
        style={{ background: "rgba(51,183,109,0.16)", color: "#33b76d", border: "1px solid #33b76d55" }}>
        {loading ? <Sparkles size={15} className="animate-pulse" /> : <Activity size={15} />}
        {loading ? "Computing…" : "Calculate Coal Requirement"}
      </button>

      {result && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-4 space-y-3">
          <div className="panel-2 px-4 py-4 text-center">
            <div className="eyebrow">Coal Needed</div>
            <div className="text-5xl font-bold tnum text-brand-green mt-1">
              <AnimatedNumber value={result.coal_needed_mt} decimals={1} /> <span className="text-lg text-ink-soft">MT</span>
            </div>
            <div className="text-[0.7rem] font-mono text-ink-soft mt-1">Confidence {fmtNum(result.confidence, 1)}% · Physics baseline {fmtNum(result.physics_coal_mt, 1)} MT</div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Mini label="Expected Eff." value={`${fmtNum(result.expected_boiler_eff, 1)}%`} />
            <Mini label="Expected Cost" value={fmtINR(result.expected_cost)} />
            <Mini label="Steam Req." value={`${fmtNum(result.steam_required_mt)} MT`} />
          </div>
          <div className="panel-2 px-3 py-3">
            <div className="eyebrow mb-2">Recommended Coal Blend</div>
            <div className="space-y-2">
              {result.recommended_blend.map((b) => (
                <div key={b.id} className="flex items-center gap-2">
                  <span className="text-[0.76rem] text-ink w-24">{b.name}</span>
                  <div className="flex-1 h-2.5 bg-[#16223d]"><div className="h-full bg-brand-green" style={{ width: `${b.share}%` }} /></div>
                  <span className="text-[0.72rem] font-mono tnum text-ink w-10 text-right">{b.share}%</span>
                  <span className="text-[0.66rem] font-mono text-ink-soft w-20 text-right">{fmtNum(b.quantity_mt, 1)} MT</span>
                </div>
              ))}
            </div>
          </div>
          <div className="panel-2 px-4 py-3 flex items-center justify-between border-l-2" style={{ borderLeftColor: "#33b76d" }}>
            <span className="eyebrow" style={{ color: "#33b76d" }}>Expected Savings</span>
            <span className="text-xl font-bold tnum text-brand-green">{fmtLakh(result.expected_savings_per_day)}/day</span>
          </div>
        </motion.div>
      )}
    </Panel>
  );
}

function DeviationInvestigation() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = () => api.deviation().then(setData).catch(() => {});
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);
  if (!data) return <Panel eyebrow="Aviation-style RCA" title="Deviation Investigation"><div className="text-ink-soft font-mono text-sm">Loading…</div></Panel>;

  const rc = data.root_cause;
  return (
    <Panel eyebrow="Aviation-style RCA" title="Deviation Investigation" testid="deviation-investigation"
      right={<TrendingDown size={16} className="text-alert" />}>
      <div className="panel-2 px-4 py-3 mb-3 flex items-center justify-between">
        <div>
          <div className="eyebrow">Coal Variance</div>
          <div className="text-2xl font-bold tnum" style={{ color: rc.variance_pct > 10 ? "#E63946" : rc.variance_pct > 4 ? "#fab20b" : "#33b76d" }}>
            {signPct(rc.variance_pct)}
          </div>
        </div>
        <div className="text-right">
          <div className="eyebrow">Potential Loss</div>
          <div className="text-lg font-bold tnum text-alert">{fmtLakh(rc.potential_loss)}</div>
        </div>
      </div>

      <div className="eyebrow mb-2">Root Cause Ranking</div>
      <div className="space-y-2 mb-3">
        {rc.causes.map((c, i) => {
          const col = i === 0 ? "#E63946" : i === 1 ? "#fab20b" : "#8BA1C0";
          return (
            <div key={c.cause} className="flex items-center gap-2">
              <span className="text-[0.74rem] text-ink w-40 truncate">{c.cause}</span>
              <div className="flex-1 h-2.5 bg-[#16223d]">
                <motion.div className="h-full" style={{ background: col }} initial={{ width: 0 }} animate={{ width: `${c.probability}%` }} transition={{ duration: 0.6 }} />
              </div>
              <span className="text-[0.74rem] font-mono tnum w-9 text-right" style={{ color: col }}>{c.probability}%</span>
            </div>
          );
        })}
      </div>

      {data.recommendations.length > 0 && (
        <>
          <div className="eyebrow mb-2">Recommended Actions</div>
          <div className="space-y-2">
            {data.recommendations.slice(0, 3).map((r, i) => (
              <div key={i} className="panel-2 px-3 py-2.5 border-l-2" style={{ borderLeftColor: r.priority === "High" ? "#E63946" : r.priority === "Medium" ? "#fab20b" : "#8BA1C0" }}>
                <div className="flex items-center justify-between">
                  <span className="text-[0.78rem] font-medium text-ink">{r.cause}</span>
                  <span className="text-[0.6rem] font-mono px-1.5 py-0.5" style={{ color: r.priority === "High" ? "#E63946" : "#fab20b", border: "1px solid currentColor" }}>{r.priority}</span>
                </div>
                <div className="text-[0.72rem] text-ink-soft mt-1">{r.action}</div>
                <div className="flex gap-4 mt-1.5 text-[0.66rem] font-mono">
                  <span className="text-ink-soft">Impact <span className="text-alert">{fmtNum(r.impact_mt, 1)} MT</span></span>
                  <span className="text-ink-soft">Save <span className="text-brand-green">{fmtLakh(r.estimated_savings)}</span></span>
                  <span className="text-ink-soft">Difficulty <span className="text-ink">{r.difficulty}</span></span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </Panel>
  );
}

function AdvisorChat() {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "I'm your OIM-AKRY race engineer. Ask me about coal, boiler efficiency, suppliers, or why we're off target today." },
  ]);
  const [input, setInput] = useState("");
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || busy) return;
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setBusy(true);
    try {
      const r = await api.aiChat({ session_id: session, message: msg });
      setSession(r.session_id);
      setMessages((m) => [...m, { role: "assistant", text: r.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "I couldn't reach the model just now — the dashboards still hold the live numbers." }]);
    } finally { setBusy(false); }
  };

  const suggestions = ["Why are we over budget today?", "Which supplier should I buy?", "How do I improve boiler efficiency?"];

  return (
    <Panel eyebrow="Conversational" title="Ask the AI Advisor" testid="advisor-chat"
      right={<span className="text-[0.62rem] font-mono px-2 py-0.5 border border-brand-green/40 text-brand-green uppercase tracking-wider">Llama 3 · grounded</span>}>
      <div className="h-72 overflow-y-auto space-y-3 mb-3 pr-1" data-testid="chat-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] px-3.5 py-2.5 text-[0.82rem] leading-relaxed ${m.role === "user" ? "bg-brand-blue/30 text-ink border border-brand-blue/40" : "panel-2 text-ink/90"}`}>
              {m.text}
            </div>
          </div>
        ))}
        {busy && <div className="flex justify-start"><div className="panel-2 px-3.5 py-2.5 text-ink-soft text-[0.8rem] inline-flex items-center gap-2"><Sparkles size={13} className="animate-pulse text-brand-green" /> Thinking…</div></div>}
        <div ref={endRef} />
      </div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {suggestions.map((s) => (
          <button key={s} onClick={() => setInput(s)} className="text-[0.66rem] font-mono px-2 py-1 panel-2 text-ink-soft hover:text-brand-green hover:border-brand-green/50 transition-colors">{s}</button>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          data-testid="chat-input" placeholder="Ask about coal, steam, suppliers, cost, carbon…"
          className="flex-1 panel-2 px-3.5 py-2.5 text-ink text-sm focus:outline-none focus:border-brand-green" />
        <button onClick={send} disabled={busy} data-testid="chat-send"
          className="px-4 flex items-center justify-center bg-brand-green text-[#04210f] font-bold hover:opacity-90 transition-opacity">
          <Send size={16} />
        </button>
      </div>
    </Panel>
  );
}

function Mini({ label, value }) {
  return (
    <div className="panel-2 px-2.5 py-2 text-center">
      <div className="eyebrow truncate">{label}</div>
      <div className="text-sm font-bold tnum text-ink mt-0.5">{value}</div>
    </div>
  );
}
